import type {
  BuildingInteriorId,
  InteriorAreaDoc,
  InteriorKit,
  OutdoorAreaDoc,
  OutdoorAreaId,
  VillageMapAnimal,
  VillageMapAnimalCatalogEntry,
  VillageMapBuilding,
  VillageMapDir,
  VillageMapDocument,
  VillageMapLegend,
  VillageMapLegendCategory,
  VillageMapLegendEntry,
  VillageMapNpc,
  VillageMapNpcCatalogEntry,
  VillageMapSign,
} from '@/lib/admin-types';
import { ApiError } from '@/lib/api';
import {
  MAP_STAMP_BY_ID,
  stampWriteCells,
} from '@/lib/village-map-stamps';

/** Max characters for a custom map sign (matches API validation). */
export const MAX_SIGN_TEXT_LENGTH = 120;

/** Max characters for a designer NPC note (matches API validation). */
export const MAX_NPC_NOTE_LENGTH = 120;

/** Max characters for a designer animal note (matches API validation). */
export const MAX_ANIMAL_NOTE_LENGTH = 120;

/** Max characters for a furniture word id (matches API validation). */
export const MAX_FURNITURE_WORD_LENGTH = 64;

/** Keep in sync with `services/village_map_service.py` BUILDING_INTERIOR_IDS. */
export const BUILDING_INTERIOR_IDS: BuildingInteriorId[] = [
  'home-pog-yawg',
  'home-player',
  'home-niam-txiv',
  'shop-clothing',
  'school',
];

export const INTERIOR_KIND_BY_ID: Record<
  BuildingInteriorId,
  InteriorAreaDoc['kind']
> = {
  'home-pog-yawg': 'house',
  'home-player': 'house',
  'home-niam-txiv': 'house',
  'shop-clothing': 'shop',
  school: 'school',
};

export const INTERIOR_KITS: InteriorKit[] = ['gold', 'moss', 'slate'];

export function interiorFloorKitOf(room: InteriorAreaDoc): InteriorKit {
  return room.floorKit ?? room.kit;
}

export function interiorRugKitOf(room: InteriorAreaDoc): InteriorKit {
  return room.rugKit ?? room.kit;
}

export const INTERIOR_WIDTH = 10;
export const INTERIOR_HEIGHT = 8;

const INTERIOR_WALKABLE = new Set(['=', '~', 'E']);
const INTERIOR_FURNITURE = new Set([
  'b',
  't',
  'c',
  's',
  'h',
  'x',
  'o',
  'l',
  'j',
  'k',
  'f',
  'p',
  'r',
  'm',
  'a',
  'A',
  'd',
  'v',
  'g',
  'n',
  'q',
  'y',
  'z',
]);

/**
 * Outdoor NPC catalog — keep in sync with mobile `VILLAGE_NPCS` ids/names/sprites
 * and `services/village_map_service.py` KNOWN_OUTDOOR_NPCS.
 */
export const OUTDOOR_NPC_CATALOG: VillageMapNpcCatalogEntry[] = [
  { id: 'pog', name: 'Pog', sprite: 'npcPog' },
  { id: 'yawg', name: 'Yawg', sprite: 'npcYawg' },
  { id: 'tub', name: 'Kwv', sprite: 'npcTub' },
  { id: 'niam', name: 'Niam', sprite: 'npcNiam' },
  { id: 'txiv', name: 'Txiv ntxawm', sprite: 'npcTxiv' },
  { id: 'maiv', name: 'Maiv', sprite: 'npcMaiv' },
  { id: 'npawg', name: 'Npawg', sprite: 'npcNpawg' },
];

export const OUTDOOR_NPC_BY_ID: Record<string, VillageMapNpcCatalogEntry> =
  Object.fromEntries(OUTDOOR_NPC_CATALOG.map((n) => [n.id, n]));

/**
 * Outdoor animal kind catalog — keep in sync with mobile `ANIMAL_KIND_TEMPLATES`
 * / `VILLAGE_ANIMALS` kinds and `services/village_map_service.py` KNOWN_OUTDOOR_ANIMALS.
 * Catalog ids are kinds; map docs place many instances per kind.
 */
export const OUTDOOR_ANIMAL_CATALOG: VillageMapAnimalCatalogEntry[] = [
  { id: 'qaib', name: 'Chicken', sprite: 'chicken0' },
  { id: 'npua', name: 'Pig', sprite: 'pig0' },
  { id: 'aub', name: 'Dog', sprite: 'dog' },
  { id: 'os', name: 'Duck', sprite: 'duck' },
  { id: 'twm', name: 'Buffalo', sprite: 'buffalo' },
  { id: 'miv', name: 'Cat', sprite: 'cat' },
  { id: 'nees', name: 'Horse', sprite: 'horse' },
  { id: 'yaj', name: 'Sheep', sprite: 'sheep' },
  { id: 'nyuj', name: 'Cow', sprite: 'cow' },
  { id: 'luav', name: 'Rabbit', sprite: 'rabbit' },
  { id: 'ntses', name: 'Fish', sprite: 'fishRipple' },
  { id: 'noog', name: 'Bird', sprite: 'bird' },
  { id: 'muv', name: 'Bee', sprite: 'bee' },
  { id: 'npauj-npaim', name: 'Butterfly', sprite: 'butterfly' },
  { id: 'qav', name: 'Frog', sprite: 'frog' },
  { id: 'nab', name: 'Snake', sprite: 'snake' },
];

export const OUTDOOR_ANIMAL_BY_ID: Record<string, VillageMapAnimalCatalogEntry> =
  Object.fromEntries(OUTDOOR_ANIMAL_CATALOG.map((a) => [a.id, a]));

export const OUTDOOR_AREA_IDS: OutdoorAreaId[] = [
  'town',
  'farm',
  'passage',
  'mountain',
  'largeTown',
  'greatCity',
];

export const AREA_DIMENSIONS: Record<OutdoorAreaId, { width: number; height: number }> = {
  town: { width: 40, height: 38 },
  farm: { width: 30, height: 20 },
  passage: { width: 20, height: 60 },
  mountain: { width: 60, height: 72 },
  largeTown: { width: 64, height: 64 },
  greatCity: { width: 120, height: 110 },
};

export const AREA_TAB_LABEL: Record<OutdoorAreaId, string> = {
  town: 'Town',
  farm: 'Farm',
  passage: 'Bamboo pass',
  mountain: 'Mountain',
  largeTown: 'Large town',
  greatCity: 'Great city',
};

/** Interior tab labels for the admin editor (building rooms). */
export const INTERIOR_TAB_LABEL: Record<BuildingInteriorId, string> = {
  'home-pog-yawg': 'Pog & Yawg',
  'home-player': 'Player',
  'home-niam-txiv': 'Niam & Txiv ntxawm',
  'shop-clothing': 'Clothing shop',
  school: 'School',
};

export function isBuildingInteriorId(id: string): id is BuildingInteriorId {
  return (BUILDING_INTERIOR_IDS as string[]).includes(id);
}

export function isInteriorFurnitureChar(ch: string): boolean {
  return INTERIOR_FURNITURE.has(ch);
}

export function furnitureWordKey(x: number, y: number): string {
  return `${x},${y}`;
}

/** Counts from the seed outdoor maps (wip/village-sim-bundle). Soft-warning baselines. */
export const SEED_BASELINE = {
  farmPlotCount: 30,
  forageR: 9,
  forageY: 12,
} as const;

export const DIR_LABELS: Record<VillageMapDir, string> = {
  0: 'down',
  1: 'up',
  2: 'left',
  3: 'right',
};

export const CATEGORY_COLORS: Record<VillageMapLegendCategory, string> = {
  terrain: '#8BC48A',
  water: '#7FB5C9',
  farm: '#B08968',
  mountain: '#A89684',
  prop: '#5E8C61',
  forage: '#C97FA1',
  building: '#B0713A',
  structure: '#C4A574',
  furniture: '#8B6B4A',
  props: '#6B8F71',
};

export const PATH_COLOR = '#D9B98C';
export const COBBLE_COLOR = '#B4A99F';

/** Keep in sync with mobile `WALKABLE_CHARS` — `b` is a spawn marker only. */
const WALKABLE = new Set([
  '.',
  ',',
  '*',
  ':',
  '|',
  '✿',
  '❀',
  '❁',
  'P',
  'B',
  'b',
  'm',
  'v',
  'c',
]);
/** Solid farmhouse bike rack — summon interactable (not walkable). */
const BIKE_RACK_CHAR = '/';

const LEGEND_ROWS: Array<[string, string, VillageMapLegendCategory]> = [
  ['.', 'Grass', 'terrain'],
  [',', 'Grass variant', 'terrain'],
  ['*', 'Sunflower', 'terrain'],
  [':', 'Roses', 'terrain'],
  ['|', 'Lily of the valley', 'terrain'],
  ['✿', 'Tulips', 'terrain'],
  ['❀', 'Tulips (white)', 'terrain'],
  ['❁', 'Tulips (yellow)', 'terrain'],
  ['P', 'Path', 'terrain'],
  ['B', 'Bridge', 'terrain'],
  ['≠', 'Broken bridge', 'terrain'],
  ['c', 'Cobblestone', 'terrain'],
  ['W', 'Water', 'water'],
  ['@', 'Deep water', 'water'],
  ['M', 'Rice paddy', 'water'],
  ['#', 'Farm plot (soil)', 'farm'],
  ['m', 'Mountain dirt (high)', 'mountain'],
  ['v', 'Mountain low ground', 'mountain'],
  ['C', 'Cliff face', 'mountain'],
  ['X', 'Mountain rock', 'mountain'],
  ['A', 'Summit peak', 'mountain'],
  ['◈', 'Stone cairn (trail marker)', 'prop'],
  ['j', 'Cave mouth top L', 'mountain'],
  ['J', 'Cave mouth top M', 'mountain'],
  ['i', 'Cave mouth top R', 'mountain'],
  ['n', 'Cave mouth bottom L', 'mountain'],
  ['N', 'Cave mouth bottom M (walk-in)', 'mountain'],
  ['u', 'Cave mouth bottom R', 'mountain'],
  ['T', 'Tree', 'prop'],
  ['t', 'Stump', 'prop'],
  ['1', 'Banana tree', 'prop'],
  ['2', 'Bamboo', 'prop'],
  ['3', 'Palm / coconut', 'prop'],
  ['4', 'Mango tree', 'prop'],
  ['5', 'Papaya tree', 'prop'],
  ['6', 'Banyan tree', 'prop'],
  ['7', 'Bench (face down)', 'prop'],
  ['8', 'Bench (face left)', 'prop'],
  ['9', 'Bench (face right)', 'prop'],
  ['F', 'Fence', 'prop'],
  ['-', 'Fence gate (A to open)', 'prop'],
  ["'", 'Tall wooden fence', 'prop'],
  ['`', 'Padlocked wooden door (A: lock message)', 'prop'],

  ['R', 'Berry bush (forage)', 'forage'],
  ['Y', 'Fruit tree (forage)', 'forage'],
  ['[', 'Gold roof L', 'building'],
  ['l', 'Gold roof ML', 'building'],
  ['†', 'Gold roof MML', 'building'],
  ['^', 'Gold roof M', 'building'],
  ['‡', 'Gold roof MMR', 'building'],
  ['f', 'Gold roof MR', 'building'],
  [']', 'Gold roof R', 'building'],
  ['U', 'Gold wall upper', 'building'],
  ['α', 'Gold wall upper L', 'building'],
  ['β', 'Gold wall upper ML', 'building'],
  ['Ђ', 'Gold wall upper MML', 'building'],
  ['γ', 'Gold wall upper M', 'building'],
  ['Ѓ', 'Gold wall upper MMR', 'building'],
  ['δ', 'Gold wall upper MR', 'building'],
  ['ε', 'Gold wall upper R', 'building'],
  ['H', 'Gold wall', 'building'],
  ['ζ', 'Gold wall L', 'building'],
  ['η', 'Gold wall ML', 'building'],
  ['Є', 'Gold wall MML', 'building'],
  ['Љ', 'Gold wall MMR', 'building'],
  ['θ', 'Gold wall MR', 'building'],
  ['ι', 'Gold wall R', 'building'],
  ['O', 'Gold wall low', 'building'],
  ['κ', 'Gold wall low L', 'building'],
  ['λ', 'Gold wall low ML', 'building'],
  ['Њ', 'Gold wall low MML', 'building'],
  ['Ћ', 'Gold wall low MMR', 'building'],
  ['μ', 'Gold wall low MR', 'building'],
  ['ν', 'Gold wall low R', 'building'],
  ['Q', 'Gold door top', 'building'],
  ['D', 'Gold door', 'building'],
  ['{', 'Moss roof L', 'building'],
  ['$', 'Moss roof ML', 'building'],
  ['•', 'Moss roof MML', 'building'],
  ['~', 'Moss roof M', 'building'],
  ['‰', 'Moss roof MMR', 'building'],
  ['%', 'Moss roof MR', 'building'],
  ['}', 'Moss roof R', 'building'],
  ['V', 'Moss wall upper', 'building'],
  ['ξ', 'Moss wall upper L', 'building'],
  ['ο', 'Moss wall upper ML', 'building'],
  ['Ќ', 'Moss wall upper MML', 'building'],
  ['π', 'Moss wall upper M', 'building'],
  ['Ѝ', 'Moss wall upper MMR', 'building'],
  ['ρ', 'Moss wall upper MR', 'building'],
  ['ς', 'Moss wall upper R', 'building'],
  ['h', 'Moss wall', 'building'],
  ['τ', 'Moss wall L', 'building'],
  ['υ', 'Moss wall ML', 'building'],
  ['Ў', 'Moss wall MML', 'building'],
  ['Џ', 'Moss wall MMR', 'building'],
  ['φ', 'Moss wall MR', 'building'],
  ['χ', 'Moss wall R', 'building'],
  ['0', 'Moss wall low', 'building'],
  ['ψ', 'Moss wall low L', 'building'],
  ['ω', 'Moss wall low ML', 'building'],
  ['Б', 'Moss wall low MML', 'building'],
  ['Г', 'Moss wall low MMR', 'building'],
  ['À', 'Moss wall low MR', 'building'],
  ['Á', 'Moss wall low R', 'building'],
  ['y', 'Moss door top', 'building'],
  ['e', 'Moss door', 'building'],
  ['(', 'Slate roof L', 'building'],
  ['&', 'Slate roof ML', 'building'],
  ['¤', 'Slate roof MML', 'building'],
  ['_', 'Slate roof M', 'building'],
  ['¥', 'Slate roof MMR', 'building'],
  ['+', 'Slate roof MR', 'building'],
  [')', 'Slate roof R', 'building'],
  ['x', 'Slate wall upper', 'building'],
  ['Â', 'Slate wall upper L', 'building'],
  ['Ã', 'Slate wall upper ML', 'building'],
  ['Д', 'Slate wall upper MML', 'building'],
  ['Ä', 'Slate wall upper M', 'building'],
  ['Ж', 'Slate wall upper MMR', 'building'],
  ['Å', 'Slate wall upper MR', 'building'],
  ['Æ', 'Slate wall upper R', 'building'],
  ['q', 'Slate wall', 'building'],
  ['Ç', 'Slate wall L', 'building'],
  ['È', 'Slate wall ML', 'building'],
  ['З', 'Slate wall MML', 'building'],
  ['И', 'Slate wall MMR', 'building'],
  ['É', 'Slate wall MR', 'building'],
  ['Ê', 'Slate wall R', 'building'],
  [';', 'Slate wall low', 'building'],
  ['Ë', 'Slate wall low L', 'building'],
  ['Ì', 'Slate wall low ML', 'building'],
  ['Й', 'Slate wall low MML', 'building'],
  ['К', 'Slate wall low MMR', 'building'],
  ['Í', 'Slate wall low MR', 'building'],
  ['Î', 'Slate wall low R', 'building'],
  ['a', 'Slate door top', 'building'],
  ['o', 'Slate door', 'building'],
  ['<', 'Shop roof L', 'building'],
  ['=', 'Shop roof M', 'building'],
  ['>', 'Shop roof R', 'building'],
  ['p', 'Shop wall upper', 'building'],
  ['w', 'Shop wall', 'building'],
  ['z', 'Shop door top', 'building'],
  ['Z', 'Shop door', 'building'],
  ['E', 'School roof L', 'building'],
  ['G', 'School roof M', 'building'],
  ['I', 'School roof R', 'building'],
  ['r', 'School wall upper', 'building'],
  ['s', 'School wall', 'building'],
  ['g', 'School door top', 'building'],
  ['d', 'School door', 'building'],
  ['S', 'Market stall', 'building'],
  ['L', 'Well', 'prop'],
  ['!', 'Map sign (custom text)', 'prop'],
  ['?', 'Tip sign', 'prop'],
  ['✉', "Mailbox (dad's letter)", 'prop'],
  ['K', 'Boat bow', 'prop'],
  ['k', 'Boat stern', 'prop'],
  ['b', 'Bike spawn pad', 'prop'],
  [BIKE_RACK_CHAR, 'Bike rack (summon)', 'prop'],
];

export const LOCAL_LEGEND: VillageMapLegendEntry[] = LEGEND_ROWS.map(([char, label, category]) => ({
  char,
  label,
  category,
  walkable: WALKABLE.has(char),
}));

/** Keep in sync with `services/village_map_service.py` INTERIOR_LEGEND. */
const INTERIOR_LEGEND_ROWS: Array<[string, string, VillageMapLegendCategory]> = [
  ['U', 'Wall', 'structure'],
  ['w', 'Window (on wall)', 'structure'],
  ['=', 'Floor', 'structure'],
  ['~', 'Rug', 'structure'],
  ['E', 'Exit mat', 'structure'],
  ['b', 'Bed', 'furniture'],
  ['t', 'Table', 'furniture'],
  ['c', 'Chair', 'furniture'],
  ['s', 'Shelf', 'furniture'],
  ['h', 'Fireplace', 'furniture'],
  ['x', 'Chest', 'furniture'],
  ['o', 'Pot', 'furniture'],
  ['l', 'Lantern', 'furniture'],
  ['j', 'Journal', 'props'],
  ['k', 'Walking stick', 'props'],
  ['f', 'Cloth wrap', 'props'],
  ['p', 'Seed sack', 'props'],
  ['r', 'Chili string', 'props'],
  ['m', 'Farm tools', 'props'],
  ['a', 'Wall hanging', 'props'],
  ['A', 'Wall hanging 2', 'props'],
  ['d', 'Cabinet', 'furniture'],
  ['v', 'Stove', 'furniture'],
  ['g', 'Water jug', 'props'],
  ['n', 'Nightstand', 'furniture'],
  ['q', 'Wall shelf', 'furniture'],
  ['y', 'Bench (left)', 'furniture'],
  ['z', 'Bench (right)', 'furniture'],
];

export const LOCAL_INTERIOR_LEGEND: VillageMapLegendEntry[] = INTERIOR_LEGEND_ROWS.map(
  ([char, label, category]) => ({
    char,
    label,
    category,
    walkable: INTERIOR_WALKABLE.has(char),
  }),
);

const INTERIOR_LEGEND_BY_CHAR = new Map(
  LOCAL_INTERIOR_LEGEND.map((e) => [e.char, e]),
);

/** Seed defaults — keep in sync with `data/village_maps_seed.json` interiors. */
export const DEFAULT_INTERIORS: Record<BuildingInteriorId, InteriorAreaDoc> = {
  'home-pog-yawg': {
    id: 'home-pog-yawg',
    kind: 'house',
    kit: 'gold',
    width: INTERIOR_WIDTH,
    height: INTERIOR_HEIGHT,
    rows: [
      'UwUUUUUUwU',
      'UlA=h==qgU',
      'U=b====b=U',
      'U=b====b=U',
      'U=f=cc==kU',
      'U===~t===U',
      'U======yzU',
      'UUUUEUUUUU',
    ],
    spawn: { x: 4, y: 6 },
    exitCell: { x: 4, y: 7 },
    residents: [
      { npcId: 'pog', x: 2, y: 5 },
      { npcId: 'yawg', x: 6, y: 5 },
    ],
    furnitureWords: {
      '2,2': 'txaj',
      '2,3': 'txaj',
      '4,1': 'qhov-cub',
      '5,5': 'rooj',
      '7,2': 'txaj',
      '7,3': 'txaj',
      '8,1': 'hwj',
    },
  },
  'home-player': {
    id: 'home-player',
    kind: 'house',
    kit: 'moss',
    floorKit: 'gold',
    rugKit: 'gold',
    width: INTERIOR_WIDTH,
    height: INTERIOR_HEIGHT,
    rows: [
      'UwUUUUUUwU',
      'UA==h=l=oU',
      'Ubn=====vU',
      'Ub=======U',
      'U====yz==U',
      'U====~t==U',
      'Uxdp=====U',
      'UUUUEUUUUU',
    ],
    spawn: { x: 4, y: 6 },
    exitCell: { x: 4, y: 7 },
    residents: [],
    furnitureWords: {
      '1,1': 'paj',
      '1,2': 'txaj',
      '1,3': 'txaj',
      '2,6': 'txee',
      '3,6': 'noob',
      '8,2': 'qhov-cub',
      '6,5': 'rooj',
      '8,1': 'mov',
    },
  },
  'home-niam-txiv': {
    id: 'home-niam-txiv',
    kind: 'house',
    kit: 'slate',
    width: INTERIOR_WIDTH,
    height: INTERIOR_HEIGHT,
    rows: [
      'UwUUUUUUwU',
      'UAr==h==qU',
      'U=bn=====U',
      'U=b=====vU',
      'U=d=====oU',
      'U====yzc=U',
      'Umx==~t==U',
      'UUUUEUUUUU',
    ],
    spawn: { x: 4, y: 6 },
    exitCell: { x: 4, y: 7 },
    residents: [
      { npcId: 'niam', x: 3, y: 4 },
      { npcId: 'txiv', x: 8, y: 5 },
    ],
    furnitureWords: {
      '1,1': 'paj',
      '1,6': 'noob',
      '2,1': 'kua-txob',
      '2,2': 'txaj',
      '2,3': 'txaj',
      '2,4': 'txee',
      '6,6': 'rooj',
      '8,3': 'qhov-cub',
      '8,4': 'mov',
    },
  },
  'shop-clothing': {
    id: 'shop-clothing',
    kind: 'shop',
    kit: 'gold',
    width: INTERIOR_WIDTH,
    height: INTERIOR_HEIGHT,
    rows: [
      'UwUUUUUUwU',
      'U========U',
      'U=s====s=U',
      'U====t===U',
      'U=c======U',
      'U==~~~~==U',
      'U=f====f=U',
      'UUUUEUUUUU',
    ],
    spawn: { x: 4, y: 6 },
    exitCell: { x: 4, y: 7 },
    residents: [],
    furnitureWords: {},
  },
  school: {
    id: 'school',
    kind: 'school',
    kit: 'slate',
    width: INTERIOR_WIDTH,
    height: INTERIOR_HEIGHT,
    rows: [
      'UwUUUUUUwU',
      'U========U',
      'U=s=t=s==U',
      'U=c===c==U',
      'U========U',
      'U==~~~~==U',
      'U=l======U',
      'UUUUEUUUUU',
    ],
    spawn: { x: 4, y: 6 },
    exitCell: { x: 4, y: 7 },
    residents: [],
    furnitureWords: {},
  },
};

export const LOCAL_LEGEND_MAP: VillageMapLegend = {
  legend: LOCAL_LEGEND,
  dimensions: AREA_DIMENSIONS,
  dirs: { '0': 'down', '1': 'up', '2': 'left', '3': 'right' },
  outdoorNpcs: OUTDOOR_NPC_CATALOG,
  outdoorAnimals: OUTDOOR_ANIMAL_CATALOG,
  interiorLegend: LOCAL_INTERIOR_LEGEND,
  interiorDimensions: { width: INTERIOR_WIDTH, height: INTERIOR_HEIGHT },
  interiorIds: BUILDING_INTERIOR_IDS,
  interiorKits: INTERIOR_KITS,
};

export function isInteriorWalkableChar(ch: string): boolean {
  return INTERIOR_WALKABLE.has(ch);
}

export function interiorLegendLabelForChar(
  ch: string,
  legend?: VillageMapLegendEntry[],
): string {
  if (legend?.length) {
    const hit = legend.find((e) => e.char === ch);
    if (hit) return hit.label;
  }
  return INTERIOR_LEGEND_BY_CHAR.get(ch)?.label ?? 'Unknown';
}

const LEGEND_BY_CHAR = new Map(LOCAL_LEGEND.map((e) => [e.char, e]));
const ROOF_CHARS = new Set(
  LOCAL_LEGEND.filter((e) => e.label.toLowerCase().includes('roof')).map((e) => e.char),
);
const WALL_UPPER_CHARS = new Set(
  LOCAL_LEGEND.filter((e) => /wall upper/i.test(e.label)).map((e) => e.char),
);
const DOOR_TOP_CHARS = new Set(
  LOCAL_LEGEND.filter((e) => /door top/i.test(e.label)).map((e) => e.char),
);
const DOOR_CHARS = new Set(
  LOCAL_LEGEND.filter(
    (e) =>
      e.category === 'building' &&
      /door/i.test(e.label) &&
      !/door top/i.test(e.label),
  ).map((e) => e.char),
);
const WALL_OR_DOOR = new Set(
  LOCAL_LEGEND.filter(
    (e) =>
      e.category === 'building' &&
      (/wall/i.test(e.label) || /door/i.test(e.label)),
  ).map((e) => e.char),
);
const CAVE_TOP = ['j', 'J', 'i'] as const;
const CAVE_BOT = ['n', 'N', 'u'] as const;

export function cellColor(ch: string): string {
  if (ch === 'P') return PATH_COLOR;
  if (ch === 'c') return COBBLE_COLOR;
  const entry = LEGEND_BY_CHAR.get(ch);
  if (!entry) return '#CCCCCC';
  return CATEGORY_COLORS[entry.category];
}

/** Flat legend color for an interior char (sprite-off preview). */
export function interiorCellColor(ch: string): string {
  const entry = INTERIOR_LEGEND_BY_CHAR.get(ch);
  if (!entry) return '#CCCCCC';
  return CATEGORY_COLORS[entry.category];
}

export function isWalkableChar(ch: string): boolean {
  return WALKABLE.has(ch);
}

/** Human label for a legend char (API legend preferred, then local). */
export function legendLabelForChar(
  ch: string,
  legend?: VillageMapLegendEntry[],
): string {
  if (legend?.length) {
    const hit = legend.find((e) => e.char === ch);
    if (hit) return hit.label;
  }
  return LEGEND_BY_CHAR.get(ch)?.label ?? 'Unknown';
}

/** Owned building whose stamp footprint (or door) covers this cell. */
export function buildingAtCell(
  buildings: VillageMapBuilding[] | undefined,
  x: number,
  y: number,
): VillageMapBuilding | undefined {
  if (!buildings?.length) return undefined;
  for (const b of buildings) {
    if (b.door.x === x && b.door.y === y) return b;
    const stamp = MAP_STAMP_BY_ID[b.stampId];
    if (stamp) {
      for (const cell of stampWriteCells(stamp, b.origin.x, b.origin.y)) {
        if (cell.x === x && cell.y === y) return b;
      }
      continue;
    }
    const ox = b.origin?.x ?? b.door.x - 1;
    const oy = b.origin?.y ?? b.door.y - 1;
    if (x >= ox && x < ox + 3 && y >= oy && y < oy + 4) return b;
  }
  return undefined;
}

/**
 * Hover status for the map designer, e.g.
 * `14, 10 · Path (P)` or `3, 2 · Gold door (D) · Grandma & Grandpa's house`.
 */
export function formatMapHoverReadout(opts: {
  area: OutdoorAreaDoc;
  areaId: OutdoorAreaId;
  document: VillageMapDocument;
  x: number;
  y: number;
  legend?: VillageMapLegendEntry[];
} | null): string {
  if (!opts) return 'Off map';
  const { area, areaId, document: doc, x, y, legend } = opts;
  if (x < 0 || y < 0 || x >= area.width || y >= area.height) return 'Off map';

  const ch = area.rows[y]?.[x] ?? ' ';
  const label = legendLabelForChar(ch, legend);
  const parts: string[] = [`${x}, ${y}`, `${label} (${ch})`];

  const building = buildingAtCell(area.buildings, x, y);
  if (building?.displayName) parts.push(building.displayName);

  const sign = (area.signs ?? []).find((s) => s.x === x && s.y === y);
  if (sign) {
    const text = sign.text.trim();
    parts.push(text ? `Sign “${text}”` : 'Sign');
  } else if (ch === '!') {
    parts.push('Sign');
  }

  const npc = (area.npcs ?? []).find((n) => n.x === x && n.y === y);
  if (npc) {
    const label =
      npc.name?.trim() ||
      OUTDOOR_NPC_BY_ID[npc.id]?.name ||
      npc.id;
    parts.push(`NPC ${label}`);
  }

  const animal = (area.animals ?? []).find((a) => a.x === x && a.y === y);
  if (animal) {
    const kind = animal.kind || animal.id;
    const label =
      animal.name?.trim() ||
      OUTDOOR_ANIMAL_BY_ID[kind]?.name ||
      kind;
    parts.push(`Animal ${label} (${animal.id})`);
  }

  const tip = area.tips[tipKey(x, y)];
  if (tip != null) {
    const text = tip.trim();
    parts.push(text ? `Tip “${text}”` : 'Tip');
  } else if (ch === '?') {
    parts.push('Tip');
  }

  const lockMessage = area.lockMessages?.[tipKey(x, y)];
  if (lockMessage != null) {
    const text = lockMessage.trim();
    parts.push(text ? `Lock “${text}”` : 'Lock message');
  } else if (ch === '`') {
    parts.push('Lock message');
  }

  const warp = area.edgeWarps.find((w) => w.x === x && w.y === y);
  if (warp) {
    const dest = AREA_TAB_LABEL[warp.toArea] ?? warp.toArea;
    const dir = DIR_LABELS[warp.dir] ?? String(warp.dir);
    parts.push(`Warp → ${dest} (${dir})`);
  }

  if (
    doc.playerStart.areaId === areaId &&
    doc.playerStart.x === x &&
    doc.playerStart.y === y
  ) {
    parts.push('Player start');
  }

  return parts.join(' · ');
}

export function tipKey(x: number, y: number): string {
  return `${x},${y}`;
}

export function parseTipKey(key: string): { x: number; y: number } | null {
  const m = /^(-?\d+),(-?\d+)$/.exec(key);
  if (!m) return null;
  return { x: Number(m[1]), y: Number(m[2]) };
}

export function setCellChar(rows: string[], x: number, y: number, ch: string): string[] {
  const row = rows[y];
  if (row == null || x < 0 || x >= row.length) return rows;
  if (row[x] === ch) return rows;
  const next = rows.slice();
  next[y] = row.slice(0, x) + ch + row.slice(x + 1);
  return next;
}

/** Discrete paint/eraser brush diameters (odd so the cursor stays centered). */
export const BRUSH_SIZES = [1, 3, 5] as const;
export type BrushSize = (typeof BRUSH_SIZES)[number];

export function isBrushSize(n: number): n is BrushSize {
  return (BRUSH_SIZES as readonly number[]).includes(n);
}

/**
 * Axis-aligned square footprint centered on (cx, cy), clipped to map bounds.
 * Used by paint/eraser only — stamps keep their exact pattern.
 */
export function brushFootprintCells(
  cx: number,
  cy: number,
  size: number,
  mapW: number,
  mapH: number,
): Array<{ x: number; y: number }> {
  const half = Math.max(0, Math.floor((size - 1) / 2));
  const cells: Array<{ x: number; y: number }> = [];
  for (let dy = -half; dy <= half; dy += 1) {
    for (let dx = -half; dx <= half; dx += 1) {
      const x = cx + dx;
      const y = cy + dy;
      if (x >= 0 && y >= 0 && x < mapW && y < mapH) {
        cells.push({ x, y });
      }
    }
  }
  return cells;
}

function emptyAreaDoc(id: OutdoorAreaId): OutdoorAreaDoc {
  const { width, height } = AREA_DIMENSIONS[id];
  return {
    id,
    width,
    height,
    rows: Array.from({ length: height }, () => '.'.repeat(width)),
    tips: {},
    lockMessages: {},
    edgeWarps: [],
    buildings: [],
    signs: [],
    npcs: [],
    animals: [],
  };
}

/** Upsert an outdoor NPC pin by catalog id (one spot per id on this area list). */
export function upsertNpcAt(
  npcs: VillageMapNpc[] | undefined,
  id: string,
  x: number,
  y: number,
  extras?: Partial<Pick<VillageMapNpc, 'name' | 'sprite' | 'note'>>,
): VillageMapNpc[] {
  const catalog = OUTDOOR_NPC_BY_ID[id];
  const list = [...(npcs ?? [])].filter((n) => n.id !== id);
  list.push({
    id,
    x,
    y,
    name: extras?.name ?? catalog?.name,
    sprite: extras?.sprite ?? catalog?.sprite,
    ...(extras?.note != null ? { note: extras.note } : {}),
  });
  return list;
}

export function removeNpcById(
  npcs: VillageMapNpc[] | undefined,
  id: string,
): VillageMapNpc[] {
  return (npcs ?? []).filter((n) => n.id !== id);
}

export function removeNpcAt(
  npcs: VillageMapNpc[] | undefined,
  x: number,
  y: number,
): VillageMapNpc[] {
  return (npcs ?? []).filter((n) => !(n.x === x && n.y === y));
}

/**
 * Place (or move) an NPC onto `targetArea`. Removes the id from every outdoor
 * area first so each catalog id has one outdoor spot document-wide.
 */
export function placeNpcOnDocument(
  doc: VillageMapDocument,
  targetArea: OutdoorAreaId,
  id: string,
  x: number,
  y: number,
): VillageMapDocument {
  const next = structuredClone(doc);
  const catalog = OUTDOOR_NPC_BY_ID[id];
  let existingNote: string | undefined;
  for (const areaId of OUTDOOR_AREA_IDS) {
    const area = next.outdoor[areaId];
    if (!area) continue;
    const prev = (area.npcs ?? []).find((n) => n.id === id);
    if (prev?.note != null) existingNote = prev.note;
    area.npcs = removeNpcById(area.npcs, id);
  }
  const area = next.outdoor[targetArea];
  area.npcs = upsertNpcAt(area.npcs, id, x, y, {
    name: catalog?.name,
    sprite: catalog?.sprite,
    note: existingNote,
  });
  return next;
}

/**
 * Place or move a resident NPC pin inside one interior room.
 * Unique per room (same catalog id may also exist outdoors).
 */
export function placeResidentOnInterior(
  doc: VillageMapDocument,
  interiorId: BuildingInteriorId,
  npcId: string,
  x: number,
  y: number,
): VillageMapDocument {
  const next = ensureInteriors(doc);
  const room = next.interiors[interiorId];
  if (!room) return next;
  const residents = (room.residents ?? []).filter((r) => r.npcId !== npcId);
  residents.push({ npcId, x, y });
  room.residents = residents;
  return next;
}

export function removeResidentFromInterior(
  doc: VillageMapDocument,
  interiorId: BuildingInteriorId,
  npcId: string,
): VillageMapDocument {
  const next = ensureInteriors(doc);
  const room = next.interiors[interiorId];
  if (!room) return next;
  room.residents = (room.residents ?? []).filter((r) => r.npcId !== npcId);
  return next;
}

/** Set or clear a furniture word id at (x,y) on an interior. */
export function setFurnitureWordOnInterior(
  doc: VillageMapDocument,
  interiorId: BuildingInteriorId,
  x: number,
  y: number,
  word: string,
): VillageMapDocument {
  const next = ensureInteriors(doc);
  const room = next.interiors[interiorId];
  if (!room) return next;
  const words = { ...(room.furnitureWords ?? {}) };
  const key = furnitureWordKey(x, y);
  const trimmed = word.trim();
  if (trimmed === '') delete words[key];
  else words[key] = trimmed.slice(0, MAX_FURNITURE_WORD_LENGTH);
  room.furnitureWords = words;
  return next;
}

/** Drop furnitureWords whose cells are no longer furniture chars. */
export function pruneFurnitureWords(
  room: InteriorAreaDoc,
  cells?: Array<{ x: number; y: number }>,
): Record<string, string> {
  const words = { ...(room.furnitureWords ?? {}) };
  const keys =
    cells?.map((c) => furnitureWordKey(c.x, c.y)) ?? Object.keys(words);
  for (const key of keys) {
    const parsed = parseTipKey(key);
    if (!parsed) {
      delete words[key];
      continue;
    }
    const ch = room.rows[parsed.y]?.[parsed.x];
    if (ch == null || !INTERIOR_FURNITURE.has(ch)) {
      delete words[key];
    }
  }
  return words;
}

/** One cell in a Move-tool selection (char + original position). */
export type MoveCell = { x: number; y: number; ch: string };

/** Outdoor single-cell props the Move tool can pick up. */
const OUTDOOR_MOVABLE = new Set([
  'T',
  't',
  '1',
  '2',
  '3',
  '4',
  '5',
  '6',
  '7',
  '8',
  '9',
  'F',
  '-',
  'R',
  'Y',
  'L',
  '!',
  '?',
  'X',
  'A',
  '*',
  ':',
  '|',
  '✿',
  '❀',
  '❁',
  'S',
  'K',
  'k',
  'b',
]);

export function isMovableChar(ch: string, interiorMode: boolean): boolean {
  if (interiorMode) return INTERIOR_FURNITURE.has(ch);
  return OUTDOOR_MOVABLE.has(ch);
}

/**
 * Pick a movable group under (x,y). Beds (`b`) grab all 4-connected bed cells
 * so head+foot move together; other furniture is a single cell.
 */
export function pickMovableGroup(
  rows: string[],
  x: number,
  y: number,
  interiorMode: boolean,
): MoveCell[] | null {
  const mapH = rows.length;
  const mapW = rows[0]?.length ?? 0;
  if (x < 0 || y < 0 || x >= mapW || y >= mapH) return null;
  const ch = rows[y][x];
  if (!isMovableChar(ch, interiorMode)) return null;

  if (interiorMode && ch === 'b') {
    const seen = new Set<string>();
    const stack: Array<{ x: number; y: number }> = [{ x, y }];
    const out: MoveCell[] = [];
    while (stack.length) {
      const cur = stack.pop()!;
      const key = `${cur.x},${cur.y}`;
      if (seen.has(key)) continue;
      if (cur.x < 0 || cur.y < 0 || cur.x >= mapW || cur.y >= mapH) continue;
      if (rows[cur.y][cur.x] !== 'b') continue;
      seen.add(key);
      out.push({ x: cur.x, y: cur.y, ch: 'b' });
      stack.push(
        { x: cur.x + 1, y: cur.y },
        { x: cur.x - 1, y: cur.y },
        { x: cur.x, y: cur.y + 1 },
        { x: cur.x, y: cur.y - 1 },
      );
    }
    out.sort((a, b) => (a.y !== b.y ? a.y - b.y : a.x - b.x));
    return out;
  }

  return [{ x, y, ch }];
}

/** Fill char written into vacated cells after a move. */
export function moveVacateChar(interiorMode: boolean): string {
  return interiorMode ? '=' : '.';
}

/** Whether the destination under a moved cell may be overwritten. */
export function canMoveOntoChar(ch: string, interiorMode: boolean): boolean {
  if (interiorMode) {
    return ch === '=' || ch === '~' || INTERIOR_FURNITURE.has(ch);
  }
  // Outdoor: don't stamp over water / buildings / cliff / farm plots.
  if (OUTDOOR_MOVABLE.has(ch) || WALKABLE.has(ch)) return true;
  return false;
}

/**
 * True when placing `cells` with the grabbed cell landing on (targetX,targetY)
 * stays in-bounds and only overwrites allowed chars (source cells ignored).
 */
export function moveGroupFits(
  rows: string[],
  cells: MoveCell[],
  grabX: number,
  grabY: number,
  targetX: number,
  targetY: number,
  interiorMode: boolean,
): boolean {
  const mapH = rows.length;
  const mapW = rows[0]?.length ?? 0;
  const dx = targetX - grabX;
  const dy = targetY - grabY;
  if (dx === 0 && dy === 0) return true;
  const source = new Set(cells.map((c) => `${c.x},${c.y}`));
  for (const cell of cells) {
    const nx = cell.x + dx;
    const ny = cell.y + dy;
    if (nx < 0 || ny < 0 || nx >= mapW || ny >= mapH) return false;
    if (source.has(`${nx},${ny}`)) continue;
    const dest = rows[ny][nx];
    if (!canMoveOntoChar(dest, interiorMode)) return false;
  }
  return true;
}

/** Apply a move: vacate sources, write chars at +dx/+dy. */
export function applyMoveGroup(
  rows: string[],
  cells: MoveCell[],
  dx: number,
  dy: number,
  interiorMode: boolean,
): string[] {
  if (dx === 0 && dy === 0) return rows;
  const vacate = moveVacateChar(interiorMode);
  let next = rows.slice();
  for (const cell of cells) {
    next = setCellChar(next, cell.x, cell.y, vacate);
  }
  for (const cell of cells) {
    next = setCellChar(next, cell.x + dx, cell.y + dy, cell.ch);
  }
  return next;
}

/** Remap furnitureWords for cells that moved by (dx, dy). */
export function moveFurnitureWordsForGroup(
  words: Record<string, string> | undefined,
  cells: MoveCell[],
  dx: number,
  dy: number,
): Record<string, string> {
  const next = { ...(words ?? {}) };
  if (dx === 0 && dy === 0) return next;
  const moving = new Map<string, string>();
  for (const cell of cells) {
    const key = furnitureWordKey(cell.x, cell.y);
    if (key in next) {
      moving.set(key, next[key]!);
      delete next[key];
    }
  }
  for (const cell of cells) {
    const from = furnitureWordKey(cell.x, cell.y);
    const word = moving.get(from);
    if (word != null) {
      next[furnitureWordKey(cell.x + dx, cell.y + dy)] = word;
    }
  }
  return next;
}

/**
 * Synthetic OutdoorAreaDoc so MapGrid can render an interior room
 * (residents mapped to npcs overlay).
 */
export function interiorToGridArea(room: InteriorAreaDoc): OutdoorAreaDoc {
  return {
    id: 'town',
    width: room.width,
    height: room.height,
    rows: room.rows,
    tips: {},
    lockMessages: {},
    edgeWarps: [],
    buildings: [],
    signs: [],
    npcs: (room.residents ?? []).map((r) => {
      const catalog = OUTDOOR_NPC_BY_ID[r.npcId];
      return {
        id: r.npcId,
        x: r.x,
        y: r.y,
        name: catalog?.name,
        sprite: catalog?.sprite,
      };
    }),
    animals: [],
  };
}

/** Hover readout for interior cells. */
export function formatInteriorHoverReadout(opts: {
  room: InteriorAreaDoc;
  x: number;
  y: number;
  legend?: VillageMapLegendEntry[];
} | null): string {
  if (!opts) return 'Off map';
  const { room, x, y, legend } = opts;
  if (x < 0 || y < 0 || x >= room.width || y >= room.height) return 'Off map';

  const ch = room.rows[y]?.[x] ?? ' ';
  const label = interiorLegendLabelForChar(ch, legend);
  const parts: string[] = [`${x}, ${y}`, `${label} (${ch})`];

  const resident = (room.residents ?? []).find((r) => r.x === x && r.y === y);
  if (resident) {
    const name =
      OUTDOOR_NPC_BY_ID[resident.npcId]?.name ?? resident.npcId;
    parts.push(`Resident ${name}`);
  }

  const word = room.furnitureWords?.[furnitureWordKey(x, y)];
  if (word) parts.push(`Word “${word}”`);
  else if (INTERIOR_FURNITURE.has(ch)) parts.push('Furniture');

  if (room.spawn.x === x && room.spawn.y === y) parts.push('Spawn');
  if (room.exitCell.x === x && room.exitCell.y === y) parts.push('Exit cell');

  return parts.join(' · ');
}

/** Collect every animal instance id currently used in the document. */
export function collectAnimalInstanceIds(doc: VillageMapDocument): Set<string> {
  const used = new Set<string>();
  for (const areaId of OUTDOOR_AREA_IDS) {
    for (const animal of doc.outdoor[areaId]?.animals ?? []) {
      if (animal?.id) used.add(animal.id);
    }
  }
  return used;
}

/**
 * Allocate a unique instance id for `kind`. Prefers the bare kind when free
 * (`qaib`), then `qaib-2`, `qaib-3`, …
 */
export function makeAnimalInstanceId(
  kind: string,
  usedIds: Set<string>,
): string {
  if (!usedIds.has(kind)) return kind;
  let n = 2;
  while (usedIds.has(`${kind}-${n}`)) n += 1;
  return `${kind}-${n}`;
}

/** Upsert an outdoor animal instance by instance id on this area list. */
export function upsertAnimalAt(
  animals: VillageMapAnimal[] | undefined,
  id: string,
  kind: string,
  x: number,
  y: number,
  extras?: Partial<Pick<VillageMapAnimal, 'name' | 'sprite' | 'note'>>,
): VillageMapAnimal[] {
  const catalog = OUTDOOR_ANIMAL_BY_ID[kind];
  const list = [...(animals ?? [])].filter((a) => a.id !== id);
  list.push({
    id,
    kind,
    x,
    y,
    name: extras?.name ?? catalog?.name,
    sprite: extras?.sprite ?? catalog?.sprite,
    ...(extras?.note != null ? { note: extras.note } : {}),
  });
  return list;
}

export function removeAnimalById(
  animals: VillageMapAnimal[] | undefined,
  id: string,
): VillageMapAnimal[] {
  return (animals ?? []).filter((a) => a.id !== id);
}

export function removeAnimalAt(
  animals: VillageMapAnimal[] | undefined,
  x: number,
  y: number,
): VillageMapAnimal[] {
  return (animals ?? []).filter((a) => !(a.x === x && a.y === y));
}

/**
 * Add a new animal instance of `kind` onto `targetArea`.
 * Always creates a fresh instance id (never moves an existing pin).
 * Pass `instanceId` to control the id (must be unused); otherwise one is allocated.
 */
export function addAnimalOnDocument(
  doc: VillageMapDocument,
  targetArea: OutdoorAreaId,
  kind: string,
  x: number,
  y: number,
  instanceId?: string,
): VillageMapDocument {
  const next = structuredClone(doc);
  const catalog = OUTDOOR_ANIMAL_BY_ID[kind];
  if (!catalog) return next;
  const used = collectAnimalInstanceIds(next);
  const id =
    instanceId && !used.has(instanceId)
      ? instanceId
      : makeAnimalInstanceId(kind, used);
  const area = next.outdoor[targetArea];
  area.animals = [
    ...(area.animals ?? []),
    {
      id,
      kind,
      x,
      y,
      name: catalog.name,
      sprite: catalog.sprite,
    },
  ];
  return next;
}

/**
 * Move an existing animal instance onto `targetArea` at (x,y).
 * Removes the instance from every outdoor area first, then re-adds it.
 */
export function moveAnimalOnDocument(
  doc: VillageMapDocument,
  targetArea: OutdoorAreaId,
  instanceId: string,
  x: number,
  y: number,
): VillageMapDocument {
  const next = structuredClone(doc);
  let existing: VillageMapAnimal | undefined;
  for (const areaId of OUTDOOR_AREA_IDS) {
    const area = next.outdoor[areaId];
    if (!area) continue;
    const prev = (area.animals ?? []).find((a) => a.id === instanceId);
    if (prev) existing = prev;
    area.animals = removeAnimalById(area.animals, instanceId);
  }
  if (!existing) return next;
  const kind = existing.kind || instanceId;
  const catalog = OUTDOOR_ANIMAL_BY_ID[kind];
  const area = next.outdoor[targetArea];
  area.animals = upsertAnimalAt(area.animals, instanceId, kind, x, y, {
    name: existing.name ?? catalog?.name,
    sprite: existing.sprite ?? catalog?.sprite,
    note: existing.note,
  });
  return next;
}

/**
 * @deprecated Prefer {@link addAnimalOnDocument} / {@link moveAnimalOnDocument}.
 * Kept as a thin alias: when `id` is a known kind, adds an instance; otherwise
 * moves the instance with that id.
 */
export function placeAnimalOnDocument(
  doc: VillageMapDocument,
  targetArea: OutdoorAreaId,
  id: string,
  x: number,
  y: number,
): VillageMapDocument {
  const hasInstance = OUTDOOR_AREA_IDS.some((areaId) =>
    (doc.outdoor[areaId]?.animals ?? []).some((a) => a.id === id),
  );
  if (hasInstance) {
    return moveAnimalOnDocument(doc, targetArea, id, x, y);
  }
  if (OUTDOOR_ANIMAL_BY_ID[id]) {
    return addAnimalOnDocument(doc, targetArea, id, x, y);
  }
  return doc;
}

/** Normalize legacy animal pins that used catalog id as the only identity. */
export function normalizeAnimalEntry(
  animal: VillageMapAnimal,
): VillageMapAnimal {
  const kind =
    typeof animal.kind === 'string' && animal.kind
      ? animal.kind
      : OUTDOOR_ANIMAL_BY_ID[animal.id]
        ? animal.id
        : animal.kind;
  const catalog = kind ? OUTDOOR_ANIMAL_BY_ID[kind] : undefined;
  return {
    ...animal,
    kind: kind || animal.id,
    name: animal.name ?? catalog?.name,
    sprite: animal.sprite ?? catalog?.sprite,
  };
}

export function makeSignId(x: number, y: number): string {
  return `sign-${x}-${y}`;
}

/** Upsert a sign at a cell (keeps text when re-painting `!`). */
export function upsertSignAt(
  signs: VillageMapSign[] | undefined,
  x: number,
  y: number,
  text?: string,
): VillageMapSign[] {
  const list = [...(signs ?? [])];
  const idx = list.findIndex((s) => s.x === x && s.y === y);
  if (idx >= 0) {
    if (text !== undefined) list[idx] = { ...list[idx]!, text };
    return list;
  }
  list.push({
    id: makeSignId(x, y),
    x,
    y,
    text: text ?? '',
  });
  return list;
}

export function removeSignAt(
  signs: VillageMapSign[] | undefined,
  x: number,
  y: number,
): VillageMapSign[] {
  return (signs ?? []).filter((s) => !(s.x === x && s.y === y));
}

/** Pad missing outdoor areas (e.g. older saved docs) with empty grass templates. */
export function ensureOutdoorAreas(doc: VillageMapDocument): VillageMapDocument {
  const next = structuredClone(doc);
  const outdoor = { ...next.outdoor } as Record<OutdoorAreaId, OutdoorAreaDoc>;
  for (const id of OUTDOOR_AREA_IDS) {
    if (!outdoor[id] || !Array.isArray(outdoor[id].rows)) {
      outdoor[id] = emptyAreaDoc(id);
    } else {
      // Older docs may omit signs[]; ensure array + cover existing '!' cells.
      let signs = outdoor[id].signs ?? [];
      if (!Array.isArray(signs)) signs = [];
      const covered = new Set(signs.map((s) => tipKey(s.x, s.y)));
      const rows = outdoor[id].rows;
      for (let y = 0; y < rows.length; y += 1) {
        const row = rows[y] ?? '';
        for (let x = 0; x < row.length; x += 1) {
          if (row[x] === '!' && !covered.has(tipKey(x, y))) {
            signs = upsertSignAt(signs, x, y);
            covered.add(tipKey(x, y));
          }
        }
      }
      let npcs = outdoor[id].npcs ?? [];
      if (!Array.isArray(npcs)) npcs = [];
      let animals = outdoor[id].animals ?? [];
      if (!Array.isArray(animals)) animals = [];
      animals = animals.map((a) =>
        a && typeof a === 'object' ? normalizeAnimalEntry(a) : a,
      );
      outdoor[id] = { ...outdoor[id], signs, npcs, animals };
    }
  }
  next.outdoor = outdoor;
  return ensureInteriors(next);
}

/** Fill missing interiors from seed defaults (older saved docs omit them). */
export function ensureInteriors(doc: VillageMapDocument): VillageMapDocument {
  const next = structuredClone(doc);
  const interiors = {
    ...(next.interiors ?? {}),
  } as Record<BuildingInteriorId, InteriorAreaDoc>;
  for (const id of BUILDING_INTERIOR_IDS) {
    const room = interiors[id];
    if (!room || !Array.isArray(room.rows)) {
      interiors[id] = structuredClone(DEFAULT_INTERIORS[id]);
      continue;
    }
    interiors[id] = {
      ...room,
      id,
      kind: INTERIOR_KIND_BY_ID[id],
      width: INTERIOR_WIDTH,
      height: INTERIOR_HEIGHT,
      residents: Array.isArray(room.residents) ? room.residents : [],
      furnitureWords:
        room.furnitureWords && typeof room.furnitureWords === 'object'
          ? room.furnitureWords
          : {},
    };
  }
  next.interiors = interiors;
  return next;
}

export function cloneDocument(doc: VillageMapDocument): VillageMapDocument {
  return ensureOutdoorAreas(doc);
}

export function cloneAreaRows(area: OutdoorAreaDoc): string[] {
  return area.rows.slice();
}

function countCharInArea(area: OutdoorAreaDoc, ch: string): number {
  let n = 0;
  for (const row of area.rows) {
    for (const c of row) {
      if (c === ch) n += 1;
    }
  }
  return n;
}

function countCharInDoc(doc: VillageMapDocument, ch: string): number {
  let n = 0;
  for (const id of OUTDOOR_AREA_IDS) {
    n += countCharInArea(doc.outdoor[id], ch);
  }
  return n;
}

function dirIsValid(d: unknown): d is VillageMapDir {
  return d === 0 || d === 1 || d === 2 || d === 3;
}

function isOutdoorAreaId(v: unknown): v is OutdoorAreaId {
  return (OUTDOOR_AREA_IDS as string[]).includes(v as string);
}

function warpOnEdge(
  x: number,
  y: number,
  dir: VillageMapDir,
  width: number,
  height: number,
): boolean {
  if (dir === 0) return y === height - 1;
  if (dir === 1) return y === 0;
  if (dir === 2) return x === 0;
  return x === width - 1;
}

function charAt(area: OutdoorAreaDoc, x: number, y: number): string | null {
  if (y < 0 || y >= area.height || x < 0 || x >= area.width) return null;
  return area.rows[y]?.[x] ?? null;
}

function findCaveMouthIssues(area: OutdoorAreaDoc): string[] {
  const warnings: string[] = [];
  const caveChars: Set<string> = new Set([...CAVE_TOP, ...CAVE_BOT]);
  let hasCaveChar = false;
  for (let y = 0; y < area.height; y += 1) {
    const row = area.rows[y] ?? '';
    for (let x = 0; x < area.width; x += 1) {
      if (caveChars.has(row[x] ?? '')) {
        hasCaveChar = true;
        break;
      }
    }
    if (hasCaveChar) break;
  }
  if (!hasCaveChar) return warnings;

  let foundBlock = false;
  for (let y = 0; y < area.height - 1; y += 1) {
    for (let x = 0; x < area.width - 2; x += 1) {
      const top =
        charAt(area, x, y) === CAVE_TOP[0] &&
        charAt(area, x + 1, y) === CAVE_TOP[1] &&
        charAt(area, x + 2, y) === CAVE_TOP[2];
      const bot =
        charAt(area, x, y + 1) === CAVE_BOT[0] &&
        charAt(area, x + 1, y + 1) === CAVE_BOT[1] &&
        charAt(area, x + 2, y + 1) === CAVE_BOT[2];
      if (top && bot) {
        foundBlock = true;
        break;
      }
    }
    if (foundBlock) break;
  }

  if (!foundBlock) {
    warnings.push(
      `${area.id}: cave mouth chars present but not forming the 3×2 block jJi over nNu`,
    );
  }
  return warnings;
}

function facadeWarnings(area: OutdoorAreaDoc): string[] {
  const warnings: string[] = [];
  for (let y = 0; y < area.height; y += 1) {
    const row = area.rows[y] ?? '';
    for (let x = 0; x < area.width; x += 1) {
      const ch = row[x] ?? '';
      if (ROOF_CHARS.has(ch)) {
        const below = charAt(area, x, y + 1);
        if (below == null || !WALL_UPPER_CHARS.has(below)) {
          warnings.push(
            `${area.id}: roof '${ch}' at (${x},${y}) missing upper wall below`,
          );
        }
      }
      if (WALL_UPPER_CHARS.has(ch)) {
        const above = charAt(area, x, y - 1);
        const below = charAt(area, x, y + 1);
        if (above == null || !ROOF_CHARS.has(above)) {
          warnings.push(
            `${area.id}: upper wall '${ch}' at (${x},${y}) missing roof above`,
          );
        }
        if (
          below == null ||
          !(DOOR_TOP_CHARS.has(below) || WALL_OR_DOOR.has(below))
        ) {
          warnings.push(
            `${area.id}: upper wall '${ch}' at (${x},${y}) missing door-top/wall below`,
          );
        }
      }
      if (DOOR_TOP_CHARS.has(ch)) {
        const below = charAt(area, x, y + 1);
        if (below == null || !DOOR_CHARS.has(below)) {
          warnings.push(
            `${area.id}: door-top '${ch}' at (${x},${y}) missing door below`,
          );
        }
      }
      if (DOOR_CHARS.has(ch)) {
        const above = charAt(area, x, y - 1);
        if (above == null || !DOOR_TOP_CHARS.has(above)) {
          warnings.push(
            `${area.id}: door '${ch}' at (${x},${y}) missing door-top above`,
          );
        }
      }
    }
  }
  return warnings;
}

export type VillageMapValidation = {
  errors: string[];
  warnings: string[];
};

export function validateVillageMapDocument(doc: unknown): VillageMapValidation {
  const errors: string[] = [];
  const warnings: string[] = [];

  if (doc == null || typeof doc !== 'object') {
    return { errors: ['document must be an object'], warnings };
  }

  const d = doc as Partial<VillageMapDocument>;
  try {
    if (JSON.stringify(d).length > 1_000_000) {
      errors.push('document exceeds 1 MB serialized size limit');
      return { errors, warnings };
    }
  } catch {
    errors.push('document is not JSON-serializable');
    return { errors, warnings };
  }

  if (d.version !== 1) errors.push('version must be 1');
  if (d.kind !== 'hmong-village-maps') errors.push('kind must be "hmong-village-maps"');
  if (!d.outdoor || typeof d.outdoor !== 'object') {
    errors.push(
      'outdoor must include town, farm, passage, mountain, largeTown, and greatCity',
    );
    return { errors, warnings };
  }

  for (const id of OUTDOOR_AREA_IDS) {
    if (!d.outdoor[id]) errors.push(`outdoor.${id} is required`);
  }
  if (errors.length) return { errors, warnings };

  const outdoor = d.outdoor as VillageMapDocument['outdoor'];

  for (const id of OUTDOOR_AREA_IDS) {
    const area = outdoor[id];
    const dims = AREA_DIMENSIONS[id];
    if (!area || typeof area !== 'object') {
      errors.push(`outdoor.${id} is required`);
      continue;
    }
    if (area.id !== id) errors.push(`${id}: id must be "${id}"`);
    if (area.width !== dims.width) {
      errors.push(`${id}: width must be ${dims.width}, got ${area.width}`);
    }
    if (area.height !== dims.height) {
      errors.push(`${id}: height must be ${dims.height}, got ${area.height}`);
    }
    if (!Array.isArray(area.rows)) {
      errors.push(`${id}: rows must be an array`);
      continue;
    }
    if (area.rows.length !== dims.height) {
      errors.push(`${id}: rows.length must be ${dims.height}, got ${area.rows.length}`);
    }
    for (let y = 0; y < area.rows.length; y += 1) {
      const row = area.rows[y];
      if (typeof row !== 'string') {
        errors.push(`${id}: row ${y} must be a string`);
        continue;
      }
      if (row.length !== dims.width) {
        errors.push(`${id}: row ${y} length must be ${dims.width}, got ${row.length}`);
      }
      for (let x = 0; x < row.length; x += 1) {
        const ch = row[x]!;
        if (!LEGEND_BY_CHAR.has(ch)) {
          errors.push(`${id}: unknown char '${ch}' at (${x},${y})`);
        }
      }
    }

    if (!area.tips || typeof area.tips !== 'object' || Array.isArray(area.tips)) {
      errors.push(`${id}: tips must be an object`);
    } else {
      for (const [key, text] of Object.entries(area.tips)) {
        const parsed = parseTipKey(key);
        if (!parsed) {
          errors.push(`${id}: tip key "${key}" must be "x,y"`);
          continue;
        }
        if (
          parsed.x < 0 ||
          parsed.x >= dims.width ||
          parsed.y < 0 ||
          parsed.y >= dims.height
        ) {
          errors.push(`${id}: tip key "${key}" out of bounds`);
        }
        if (typeof text !== 'string') {
          errors.push(`${id}: tip "${key}" text must be a string`);
        }
        const cell = charAt(area as OutdoorAreaDoc, parsed.x, parsed.y);
        if (cell != null && cell !== '?') {
          warnings.push(`${id}: tip at (${parsed.x},${parsed.y}) but cell is '${cell}', not '?'`);
        }
      }
      for (let y = 0; y < Math.min(area.rows.length, dims.height); y += 1) {
        const row = area.rows[y] ?? '';
        for (let x = 0; x < row.length; x += 1) {
          if (row[x] === '?' && area.tips[tipKey(x, y)] == null) {
            warnings.push(`${id}: '?' at (${x},${y}) has no tip entry`);
          }
        }
      }
    }

    const lockMessages = area.lockMessages ?? {};
    if (
      area.lockMessages != null &&
      (typeof area.lockMessages !== 'object' || Array.isArray(area.lockMessages))
    ) {
      errors.push(`${id}: lockMessages must be an object`);
    } else {
      for (const [key, text] of Object.entries(lockMessages)) {
        const parsed = parseTipKey(key);
        if (!parsed) {
          errors.push(`${id}: lockMessages key "${key}" must be "x,y"`);
          continue;
        }
        if (
          parsed.x < 0 ||
          parsed.x >= dims.width ||
          parsed.y < 0 ||
          parsed.y >= dims.height
        ) {
          errors.push(`${id}: lockMessages key "${key}" out of bounds`);
        }
        if (typeof text !== 'string') {
          errors.push(`${id}: lockMessages "${key}" text must be a string`);
        }
        const cell = charAt(area as OutdoorAreaDoc, parsed.x, parsed.y);
        if (cell != null && cell !== '`') {
          warnings.push(
            `${id}: lockMessages at (${parsed.x},${parsed.y}) but cell is '${cell}', not '\`'`,
          );
        }
      }
      for (let y = 0; y < Math.min(area.rows.length, dims.height); y += 1) {
        const row = area.rows[y] ?? '';
        for (let x = 0; x < row.length; x += 1) {
          if (row[x] === '`' && lockMessages[tipKey(x, y)] == null) {
            warnings.push(`${id}: '\`' at (${x},${y}) has no lockMessages entry`);
          }
        }
      }
    }

    if (area.signs != null && !Array.isArray(area.signs)) {
      errors.push(`${id}: signs must be an array`);
    } else {
      const seenSignCells = new Set<string>();
      for (const [i, sign] of (area.signs ?? []).entries()) {
        const label = `${id}.signs[${i}]`;
        if (!sign || typeof sign !== 'object') {
          errors.push(`${label} must be an object`);
          continue;
        }
        if (typeof sign.id !== 'string' || !sign.id) {
          errors.push(`${label}.id must be a non-empty string`);
        }
        if (
          typeof sign.x !== 'number' ||
          typeof sign.y !== 'number' ||
          !Number.isInteger(sign.x) ||
          !Number.isInteger(sign.y) ||
          sign.x < 0 ||
          sign.x >= dims.width ||
          sign.y < 0 ||
          sign.y >= dims.height
        ) {
          errors.push(`${label}: x,y out of bounds`);
        } else {
          const cellKey = tipKey(sign.x, sign.y);
          if (seenSignCells.has(cellKey)) {
            errors.push(`${label}: duplicate sign at (${sign.x},${sign.y})`);
          }
          seenSignCells.add(cellKey);
          const cell = charAt(area as OutdoorAreaDoc, sign.x, sign.y);
          if (cell != null && cell !== '!') {
            warnings.push(
              `${id}: sign at (${sign.x},${sign.y}) but cell is '${cell}', not '!'`,
            );
          }
        }
        if (typeof sign.text !== 'string') {
          errors.push(`${label}.text must be a string`);
        } else if (sign.text.length > MAX_SIGN_TEXT_LENGTH) {
          errors.push(
            `${label}.text exceeds ${MAX_SIGN_TEXT_LENGTH} characters`,
          );
        }
      }
      for (let y = 0; y < Math.min(area.rows.length, dims.height); y += 1) {
        const row = area.rows[y] ?? '';
        for (let x = 0; x < row.length; x += 1) {
          if (row[x] === '!' && !seenSignCells.has(tipKey(x, y))) {
            warnings.push(`${id}: '!' at (${x},${y}) has no signs[] entry`);
          }
        }
      }
    }

    if (area.npcs != null && !Array.isArray(area.npcs)) {
      errors.push(`${id}: npcs must be an array`);
    } else {
      const seenNpcCells = new Set<string>();
      for (const [i, npc] of (area.npcs ?? []).entries()) {
        const label = `${id}.npcs[${i}]`;
        if (!npc || typeof npc !== 'object') {
          errors.push(`${label} must be an object`);
          continue;
        }
        if (typeof npc.id !== 'string' || !npc.id) {
          errors.push(`${label}.id must be a non-empty string`);
        } else if (!OUTDOOR_NPC_BY_ID[npc.id]) {
          errors.push(`${label}.id '${npc.id}' is not a known outdoor NPC`);
        }
        if (
          typeof npc.x !== 'number' ||
          typeof npc.y !== 'number' ||
          !Number.isInteger(npc.x) ||
          !Number.isInteger(npc.y) ||
          npc.x < 0 ||
          npc.x >= dims.width ||
          npc.y < 0 ||
          npc.y >= dims.height
        ) {
          errors.push(`${label}: x,y out of bounds`);
        } else {
          const cellKey = tipKey(npc.x, npc.y);
          if (seenNpcCells.has(cellKey)) {
            errors.push(`${label}: duplicate NPC at (${npc.x},${npc.y})`);
          }
          seenNpcCells.add(cellKey);
        }
        if (npc.name != null && typeof npc.name !== 'string') {
          errors.push(`${label}.name must be a string`);
        }
        if (npc.sprite != null && typeof npc.sprite !== 'string') {
          errors.push(`${label}.sprite must be a string`);
        }
        if (npc.note != null) {
          if (typeof npc.note !== 'string') {
            errors.push(`${label}.note must be a string`);
          } else if (npc.note.length > MAX_NPC_NOTE_LENGTH) {
            errors.push(
              `${label}.note exceeds ${MAX_NPC_NOTE_LENGTH} characters`,
            );
          }
        }
      }
    }

    if (area.animals != null && !Array.isArray(area.animals)) {
      errors.push(`${id}: animals must be an array`);
    } else {
      const seenAnimalCells = new Set<string>();
      for (const [i, animal] of (area.animals ?? []).entries()) {
        const label = `${id}.animals[${i}]`;
        if (!animal || typeof animal !== 'object') {
          errors.push(`${label} must be an object`);
          continue;
        }
        if (typeof animal.id !== 'string' || !animal.id) {
          errors.push(`${label}.id must be a non-empty string`);
        }
        const kind =
          typeof animal.kind === 'string' && animal.kind
            ? animal.kind
            : typeof animal.id === 'string' && OUTDOOR_ANIMAL_BY_ID[animal.id]
              ? animal.id
              : null;
        if (!kind) {
          errors.push(
            `${label}.kind must be a non-empty string (catalog kind)`,
          );
        } else if (!OUTDOOR_ANIMAL_BY_ID[kind]) {
          errors.push(
            `${label}.kind '${kind}' is not a known outdoor animal kind`,
          );
        }
        if (
          typeof animal.x !== 'number' ||
          typeof animal.y !== 'number' ||
          !Number.isInteger(animal.x) ||
          !Number.isInteger(animal.y) ||
          animal.x < 0 ||
          animal.x >= dims.width ||
          animal.y < 0 ||
          animal.y >= dims.height
        ) {
          errors.push(`${label}: x,y out of bounds`);
        } else {
          const cellKey = tipKey(animal.x, animal.y);
          if (seenAnimalCells.has(cellKey)) {
            errors.push(
              `${label}: duplicate animal at (${animal.x},${animal.y})`,
            );
          }
          seenAnimalCells.add(cellKey);
        }
        if (animal.name != null && typeof animal.name !== 'string') {
          errors.push(`${label}.name must be a string`);
        }
        if (animal.sprite != null && typeof animal.sprite !== 'string') {
          errors.push(`${label}.sprite must be a string`);
        }
        if (animal.note != null) {
          if (typeof animal.note !== 'string') {
            errors.push(`${label}.note must be a string`);
          } else if (animal.note.length > MAX_ANIMAL_NOTE_LENGTH) {
            errors.push(
              `${label}.note exceeds ${MAX_ANIMAL_NOTE_LENGTH} characters`,
            );
          }
        }
      }
    }

    if (!Array.isArray(area.edgeWarps)) {
      errors.push(`${id}: edgeWarps must be an array`);
    } else {
      area.edgeWarps.forEach((warp, i) => {
        const label = `${id} warp[${i}]`;
        if (!dirIsValid(warp.dir) || !dirIsValid(warp.toDir)) {
          errors.push(`${label}: dir/toDir must be 0–3`);
        }
        if (!isOutdoorAreaId(warp.toArea)) {
          errors.push(`${label}: toArea is invalid`);
        }
        if (
          typeof warp.x !== 'number' ||
          typeof warp.y !== 'number' ||
          warp.x < 0 ||
          warp.x >= dims.width ||
          warp.y < 0 ||
          warp.y >= dims.height
        ) {
          errors.push(`${label}: x,y out of bounds`);
        } else if (dirIsValid(warp.dir) && !warpOnEdge(warp.x, warp.y, warp.dir, dims.width, dims.height)) {
          warnings.push(
            `${label}: source (${warp.x},${warp.y}) dir ${DIR_LABELS[warp.dir]} is not off a map edge`,
          );
        }
        if (warp.toArea === id) {
          warnings.push(`${label}: self-warp to same area`);
        }
        if (isOutdoorAreaId(warp.toArea)) {
          const toDims = AREA_DIMENSIONS[warp.toArea];
          if (
            typeof warp.toX !== 'number' ||
            typeof warp.toY !== 'number' ||
            warp.toX < 0 ||
            warp.toX >= toDims.width ||
            warp.toY < 0 ||
            warp.toY >= toDims.height
          ) {
            errors.push(`${label}: toX,toY out of bounds for ${warp.toArea}`);
          } else {
            const dest = outdoor[warp.toArea];
            const destCh = charAt(dest as OutdoorAreaDoc, warp.toX, warp.toY);
            if (destCh != null && !isWalkableChar(destCh)) {
              warnings.push(
                `${label}: destination (${warp.toX},${warp.toY}) in ${warp.toArea} is not walkable ('${destCh}')`,
              );
            }
          }
        }
      });
    }

    warnings.push(...facadeWarnings(area as OutdoorAreaDoc));
    warnings.push(...findCaveMouthIssues(area as OutdoorAreaDoc));
  }

  // One outdoor spot per NPC id across the whole document.
  const seenNpcIds = new Map<string, string>();
  for (const id of OUTDOOR_AREA_IDS) {
    const area = outdoor[id];
    if (!area || !Array.isArray(area.npcs)) continue;
    for (const [i, npc] of area.npcs.entries()) {
      if (!npc || typeof npc !== 'object' || typeof npc.id !== 'string') continue;
      const prev = seenNpcIds.get(npc.id);
      if (prev) {
        errors.push(
          `${id}.npcs[${i}]: duplicate NPC id '${npc.id}' (also in ${prev})`,
        );
      } else {
        seenNpcIds.set(npc.id, id);
      }
    }
  }

  // One outdoor spot per animal *instance* id across the whole document.
  // The same kind may appear many times / on many areas.
  const seenAnimalIds = new Map<string, string>();
  for (const id of OUTDOOR_AREA_IDS) {
    const area = outdoor[id];
    if (!area || !Array.isArray(area.animals)) continue;
    for (const [i, animal] of area.animals.entries()) {
      if (!animal || typeof animal !== 'object' || typeof animal.id !== 'string') {
        continue;
      }
      const prev = seenAnimalIds.get(animal.id);
      if (prev) {
        errors.push(
          `${id}.animals[${i}]: duplicate animal instance id '${animal.id}' (also in ${prev})`,
        );
      } else {
        seenAnimalIds.set(animal.id, id);
      }
    }
  }

  const start = d.playerStart;
  if (!start || !isOutdoorAreaId(start.areaId)) {
    errors.push('playerStart.areaId is required');
  } else {
    const dims = AREA_DIMENSIONS[start.areaId];
    if (
      typeof start.x !== 'number' ||
      typeof start.y !== 'number' ||
      start.x < 0 ||
      start.x >= dims.width ||
      start.y < 0 ||
      start.y >= dims.height
    ) {
      errors.push('playerStart is out of bounds');
    } else {
      const area = outdoor[start.areaId] as OutdoorAreaDoc;
      const ch = charAt(area, start.x, start.y);
      if (ch != null && !isWalkableChar(ch)) {
        warnings.push(
          `playerStart (${start.x},${start.y}) in ${start.areaId} is not walkable ('${ch}')`,
        );
      }
    }
  }

  const interiors = d.interiors;
  if (interiors == null || typeof interiors !== 'object' || Array.isArray(interiors)) {
    errors.push('interiors is required');
  } else {
    for (const id of BUILDING_INTERIOR_IDS) {
      const room = interiors[id];
      const label = `interiors.${id}`;
      if (!room || typeof room !== 'object') {
        errors.push(`${label} is required`);
        continue;
      }
      if (room.id !== id) errors.push(`${label}.id must be "${id}"`);
      if (room.kind !== INTERIOR_KIND_BY_ID[id]) {
        errors.push(`${label}.kind must be "${INTERIOR_KIND_BY_ID[id]}"`);
      }
      if (!INTERIOR_KITS.includes(room.kit as InteriorKit)) {
        errors.push(`${label}.kit must be gold, moss, or slate`);
      }
      if (room.width !== INTERIOR_WIDTH) {
        errors.push(`${label}.width must be ${INTERIOR_WIDTH}`);
      }
      if (room.height !== INTERIOR_HEIGHT) {
        errors.push(`${label}.height must be ${INTERIOR_HEIGHT}`);
      }
      if (!Array.isArray(room.rows)) {
        errors.push(`${label}.rows must be an array`);
        continue;
      }
      if (room.rows.length !== INTERIOR_HEIGHT) {
        errors.push(
          `${label}.rows.length must be ${INTERIOR_HEIGHT}, got ${room.rows.length}`,
        );
      }
      let exitCount = 0;
      for (let y = 0; y < room.rows.length; y += 1) {
        const row = room.rows[y];
        if (typeof row !== 'string') {
          errors.push(`${label}.rows[${y}] must be a string`);
          continue;
        }
        if (row.length !== INTERIOR_WIDTH) {
          errors.push(
            `${label}.rows[${y}] length must be ${INTERIOR_WIDTH}, got ${row.length}`,
          );
        }
        for (let x = 0; x < row.length; x += 1) {
          const ch = row[x]!;
          if (!INTERIOR_LEGEND_BY_CHAR.has(ch)) {
            errors.push(`${label}: unknown char '${ch}' at (${x},${y})`);
          }
          if (ch === 'E') exitCount += 1;
        }
      }
      if (room.rows.length > 0 && exitCount < 1) {
        errors.push(`${label}: at least one exit cell 'E' is required`);
      }

      const spawn = room.spawn;
      if (!spawn || typeof spawn.x !== 'number' || typeof spawn.y !== 'number') {
        errors.push(`${label}.spawn.x and .y must be numbers`);
      } else if (
        spawn.x < 0 ||
        spawn.x >= INTERIOR_WIDTH ||
        spawn.y < 0 ||
        spawn.y >= INTERIOR_HEIGHT
      ) {
        errors.push(`${label}.spawn is out of bounds`);
      } else {
        const ch = room.rows[spawn.y]?.[spawn.x];
        if (ch != null && !isInteriorWalkableChar(ch)) {
          errors.push(
            `${label}.spawn (${spawn.x},${spawn.y}) must be walkable (=, ~, or E)`,
          );
        }
      }

      const exitCell = room.exitCell;
      if (
        !exitCell ||
        typeof exitCell.x !== 'number' ||
        typeof exitCell.y !== 'number'
      ) {
        errors.push(`${label}.exitCell.x and .y must be numbers`);
      } else if (
        exitCell.x < 0 ||
        exitCell.x >= INTERIOR_WIDTH ||
        exitCell.y < 0 ||
        exitCell.y >= INTERIOR_HEIGHT
      ) {
        errors.push(`${label}.exitCell is out of bounds`);
      } else {
        const ch = room.rows[exitCell.y]?.[exitCell.x];
        if (ch != null && ch !== 'E') {
          errors.push(
            `${label}.exitCell (${exitCell.x},${exitCell.y}) must be an 'E' cell`,
          );
        }
      }

      const residents = room.residents ?? [];
      if (!Array.isArray(residents)) {
        errors.push(`${label}.residents must be an array`);
      } else {
        const seenIds = new Set<string>();
        for (const [ri, resident] of residents.entries()) {
          const rlabel = `${label}.residents[${ri}]`;
          if (!resident || typeof resident !== 'object') {
            errors.push(`${rlabel} must be an object`);
            continue;
          }
          if (typeof resident.npcId !== 'string' || !resident.npcId) {
            errors.push(`${rlabel}.npcId must be a non-empty string`);
          } else if (!OUTDOOR_NPC_BY_ID[resident.npcId]) {
            errors.push(`${rlabel}.npcId '${resident.npcId}' is not a known outdoor NPC`);
          } else if (seenIds.has(resident.npcId)) {
            errors.push(`${rlabel}: duplicate npcId '${resident.npcId}'`);
          } else {
            seenIds.add(resident.npcId);
          }
          if (typeof resident.x !== 'number' || typeof resident.y !== 'number') {
            errors.push(`${rlabel}.x and .y must be numbers`);
          } else if (
            resident.x < 0 ||
            resident.x >= INTERIOR_WIDTH ||
            resident.y < 0 ||
            resident.y >= INTERIOR_HEIGHT
          ) {
            errors.push(`${rlabel} is out of bounds`);
          }
        }
      }

      const furnitureWords = room.furnitureWords ?? {};
      if (
        furnitureWords == null ||
        typeof furnitureWords !== 'object' ||
        Array.isArray(furnitureWords)
      ) {
        errors.push(`${label}.furnitureWords must be an object`);
      } else {
        for (const [key, word] of Object.entries(furnitureWords)) {
          if (typeof word !== 'string') {
            errors.push(`${label}.furnitureWords values must be strings`);
            continue;
          }
          if (word.length > MAX_FURNITURE_WORD_LENGTH) {
            errors.push(
              `${label}.furnitureWords["${key}"] exceeds ${MAX_FURNITURE_WORD_LENGTH} characters`,
            );
          }
          const parsed = parseTipKey(key);
          if (!parsed) {
            errors.push(`${label}.furnitureWords key "${key}" must be "x,y"`);
            continue;
          }
          if (
            parsed.x < 0 ||
            parsed.x >= INTERIOR_WIDTH ||
            parsed.y < 0 ||
            parsed.y >= INTERIOR_HEIGHT
          ) {
            errors.push(`${label}.furnitureWords key "${key}" out of bounds`);
            continue;
          }
          const ch = room.rows[parsed.y]?.[parsed.x];
          if (ch != null && !INTERIOR_FURNITURE.has(ch)) {
            warnings.push(
              `${label}: furnitureWord at (${parsed.x},${parsed.y}) but cell is '${ch}', not furniture`,
            );
          }
        }
      }
    }
  }

  if (errors.length === 0) {
    const full = d as VillageMapDocument;
    const farmPlots = countCharInArea(full.outdoor.farm, '#');
    if (farmPlots !== SEED_BASELINE.farmPlotCount) {
      warnings.push(
        `farm '#' plot count is ${farmPlots} (seed has ${SEED_BASELINE.farmPlotCount}) — may break save plot indices`,
      );
    }
    const rCount = countCharInDoc(full, 'R');
    const yCount = countCharInDoc(full, 'Y');
    if (rCount !== SEED_BASELINE.forageR) {
      warnings.push(
        `forage 'R' count is ${rCount} (seed has ${SEED_BASELINE.forageR}) — may break forage save indices`,
      );
    }
    if (yCount !== SEED_BASELINE.forageY) {
      warnings.push(
        `forage 'Y' count is ${yCount} (seed has ${SEED_BASELINE.forageY}) — may break forage save indices`,
      );
    }
  }

  return { errors, warnings };
}

export function parseVillageMapSaveError(err: unknown): {
  stale: boolean;
  errors: string[];
  message: string;
} {
  if (err instanceof ApiError) {
    if (err.status === 409) {
      return {
        stale: true,
        errors: [],
        message: 'This map changed elsewhere — reload before saving',
      };
    }
    if (err.status === 422 && err.bodyText) {
      try {
        const body = JSON.parse(err.bodyText) as {
          errors?: string[];
          detail?: unknown;
        };
        if (Array.isArray(body.errors) && body.errors.length) {
          return { stale: false, errors: body.errors, message: body.errors.join('; ') };
        }
        // FastAPI default wraps payloads as `{ detail: { errors, warnings } }`
        if (
          body.detail &&
          typeof body.detail === 'object' &&
          Array.isArray((body.detail as { errors?: unknown }).errors)
        ) {
          const nested = (body.detail as { errors: string[] }).errors;
          if (nested.length) {
            return { stale: false, errors: nested, message: nested.join('; ') };
          }
        }
        if (typeof body.detail === 'string') {
          return { stale: false, errors: [body.detail], message: body.detail };
        }
      } catch {
        /* fall through */
      }
    }
    return {
      stale: false,
      errors: err.detail ? [err.detail] : [],
      message: err.detail ?? err.message,
    };
  }
  if (err instanceof Error) {
    return { stale: false, errors: [err.message], message: err.message };
  }
  return { stale: false, errors: [], message: 'Save failed' };
}
